/**
 * OngletOffresIndemnisation.js - Version professionnelle
 *
 * Palette monochrome + accent vert unique, layout panneaux structurés,
 * icônes AntD exclusivement, zéro emoji.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table, Tag, Button, Modal, Form, DatePicker, Select,
    Spin, Empty, Divider, Tooltip, Space, Alert, Row, Col, Popover,
    Collapse, Typography, Popconfirm, Badge, Tabs,
} from 'antd';
import CBInputNumber from '../../../components/CBInputNumber';
import { OPTIONS_TAUX_RC } from '../../../components/ChampResponsabilite';
import {
    CalculatorOutlined, CheckCircleOutlined, PrinterOutlined,
    PlusOutlined, MedicineBoxOutlined,
    InfoCircleOutlined, DeleteOutlined, EditOutlined, TeamOutlined,
    FileProtectOutlined, WarningOutlined, SafetyOutlined,
    ExclamationCircleOutlined, UserOutlined, CalendarOutlined,
    DollarOutlined, AuditOutlined, FileTextOutlined,
    ClockCircleOutlined, CarOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import axiosInstance from '../../../variables/api/axiosInstance';
import { formatDate, formatMontant, estVictimeIndemnisable, STATUTS_ACTIVITE } from '../../../variables/constants';
import WorkflowOffre, { uploadGed, ouvrirImpressionOffre } from './WorkflowOffre';
import GedFileField from '../../../components/GedFileField';
import dayjs from 'dayjs';
import i18n from '../../../i18n';
import { localeIntl } from '../../../i18n/format';
import { avecLibelleTraduit } from '../../../i18n/libelles';


const { Text } = Typography;

//  Design tokens 
const ACCENT = '#1a6b3c';
const ACCENT_LIGHT = '#f0fdf4';
const ACCENT_BORDER = '#d1fae5';
const NEUTRAL_50 = '#fafafa';
const NEUTRAL_100 = '#f4f4f5';
const NEUTRAL_200 = '#e4e4e7';
const NEUTRAL_400 = '#a1a1aa';
const NEUTRAL_600 = '#52525b';
const NEUTRAL_900 = '#18181b';
const DANGER = '#dc2626';
const WARN_BG = '#fffbeb';
const WARN_BORDER = '#fde68a';
const WARN_TEXT = '#92400e';

//  Constantes 
const PRETIUM_OPTIONS = [
    { value: 'NEANT', label: 'Néant (0 %)' },
    { value: 'TRES_LEGER', label: 'Très léger (5 %)' },
    { value: 'LEGER', label: 'Léger (10 %)' },
    { value: 'MODERE', label: 'Modéré (20 %)' },
    { value: 'MOYEN', label: 'Moyen (40 %)' },
    { value: 'ASSEZ_IMPORTANT', label: 'Assez important (60 %)' },
    { value: 'IMPORTANT', label: 'Important (100 %)' },
    { value: 'TRES_IMPORTANT', label: 'Très important (150 %)' },
    { value: 'EXCEPTIONNEL', label: 'Exceptionnel (300 %)' },
];

const LIEN_OPTIONS = avecLibelleTraduit([
    { value: 'CONJOINT' },
    { value: 'ENFANT' },
    { value: 'PERE' },
    { value: 'MERE' },
    { value: 'FRERE' },
    { value: 'SOEUR' },
    { value: 'CONCUBINE' },
    { value: 'LESE_CHARGE' },
], 'dossierFinance:offres.liens');

const statutConfig = (offre) => {
    if (!offre) return { color: 'default', label: i18n.t('dossierFinance:offres.statut.sansOffre') };
    if (offre.dateValidation) return { color: 'success', label: i18n.t('dossierFinance:offres.statut.validee') };
    return { color: 'processing', label: i18n.t('dossierFinance:offres.statut.enAttente') };
};

//  Styles partagés 
const panelStyle = {
    border: `1px solid ${NEUTRAL_200}`,
    borderRadius: 8,
    marginBottom: 12,
    background: '#fff',
    overflow: 'hidden',
};

const sectionLabel = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: NEUTRAL_400,
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
};

const amountCard = (value, label, danger = false) => (
    <div style={{
        padding: '10px 16px',
        background: danger ? '#fff5f5' : ACCENT_LIGHT,
        border: `1px solid ${danger ? '#fca5a5' : ACCENT_BORDER}`,
        borderRadius: 6,
        minWidth: 160,
    }}>
        <div style={{ fontSize: 11, color: NEUTRAL_600, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: danger ? DANGER : ACCENT, fontVariantNumeric: 'tabular-nums' }}>
            {formatMontant(value)}
        </div>
    </div>
);

/* Partage des responsabilités (GET /v1/sinistres/{id}/partage-responsabilite) :
   la responsabilité fait 100 % et se partage entre les parties ; la part de l'assuré
   est ce qui reste, et le taux d'une victime ne dépasse jamais 100 % moins sa faute. */
const partDeLaVictime = (partage, victime) =>
    partage?.parties?.find(p => p.victimeTrackingId === victime?.victimeTrackingId) || null;

const fmtTaux = (t) => (t === null || t === undefined ? '-' : `${Number(t)} %`);

/** D'où vient le taux appliqué à une victime, en une phrase. */
const origineTaux = (partage, part) => {
    if (!partage) return null;
    const faute = Number(part?.taux) || 0;
    if (partage.origine === 'PARTIES') {
        return i18n.t('dossierFinance:offres.origineTaux.parties', { taux: fmtTaux(partage.totalParties) });
    }
    const base = partage.origine === 'DOSSIER' ? i18n.t('dossierFinance:offres.origineTaux.dossier', { taux: fmtTaux(partage.partAssure) }) : i18n.t('dossierFinance:offres.origineTaux.aucune');
    return faute > 0 ? i18n.t('dossierFinance:offres.origineTaux.reduit', { base, faute: fmtTaux(faute) }) : base;
};

//  Bandeau taux RC ─
const BandeauTauxRc = ({ sinistre, partage, victime, tauxRcOverride }) => {
    const { t } = useTranslation('dossierFinance');
    const part = partDeLaVictime(partage, victime);
    const taux = tauxRcOverride ?? part?.tauxApplique ?? sinistre?.tauxRc;

    if (partage?.depasse100 && tauxRcOverride == null) return (
        <Alert
            type="error"
            showIcon
            style={{ borderRadius: 6, marginBottom: 12, fontSize: 12 }}
            message={t('offres.bandeauRc.incoherent', { taux: fmtTaux(partage.totalParties) })}
            description={t('offres.bandeauRc.incoherentTexte')}
        />
    );

    if (taux === null || taux === undefined || (partage?.origine === 'DEFAUT' && tauxRcOverride == null && !(Number(part?.taux) > 0))) return (
        <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ borderRadius: 6, marginBottom: 12, fontSize: 12 }}
            message={t('offres.bandeauRc.nonRenseigne')}
            description={t('offres.bandeauRc.nonRenseigneTexte')}
        />
    );

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px',
            background: NEUTRAL_50,
            border: `1px solid ${NEUTRAL_200}`,
            borderRadius: 6,
            marginBottom: 12,
        }}>
            <SafetyOutlined style={{ color: NEUTRAL_600, fontSize: 14 }} />
            <span style={{ fontSize: 13, color: NEUTRAL_900 }}>
                {t('offres.bandeauRc.tauxRc')}&nbsp;<strong style={{ color: ACCENT }}>{taux} %</strong>
            </span>
            {tauxRcOverride != null && (
                <Tag color="blue" style={{ borderRadius: 4, fontSize: 11, marginLeft: 4 }}>{t('offres.bandeauRc.override')}</Tag>
            )}
            {!tauxRcOverride && sinistre?.positionRc && (
                <Tag style={{ borderRadius: 4, fontSize: 11 }}>{sinistre.positionRc.replace(/_/g, ' ')}</Tag>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 11, color: NEUTRAL_400 }}>
                {tauxRcOverride != null
                    ? t('offres.bandeauRc.tauxFixe')
                    : origineTaux(partage, part) || (taux < 100 ? t('offres.bandeauRc.partAssuree', { taux }) : t('offres.bandeauRc.responsabiliteTotale'))}
            </span>
        </div>
    );
};

/* Le taux enregistré sur l'offre est la part de l'assuré, APPLIQUÉE au total brut :
   « Déduction RC (25 %) » à côté d'un retrait de 75 % se lisait à l'envers. */
const libelleDeductionRc = (taux) => {
    const applique = taux == null ? 100 : Number(taux);
    return i18n.t('dossierFinance:offres.deductionRc', { deduction: 100 - applique, applique });
};

//  Partage des responsabilités du dossier ─
const ResumePartage = ({ partage }) => {
    const { t } = useTranslation('dossierFinance');
    if (!partage) return null;
    const responsables = (partage.parties || []).filter(p => Number(p.taux) > 0);
    const sansTaux = partage.partieAdverseSansTaux || [];
    return (
        <div style={{
            padding: '10px 14px', marginBottom: 16, borderRadius: 6,
            background: partage.depasse100 ? '#fef2f2' : NEUTRAL_50,
            border: `1px solid ${partage.depasse100 ? '#fecaca' : NEUTRAL_200}`,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <SafetyOutlined style={{ color: NEUTRAL_600 }} />
                <span style={{ fontSize: 13, color: NEUTRAL_900 }}>
                    {t('offres.partage.partAssure')}&nbsp;
                    <strong style={{ color: partage.depasse100 ? DANGER : ACCENT }}>{fmtTaux(partage.partAssure)}</strong>
                </span>
                <Tag style={{ borderRadius: 4, fontSize: 11 }}>
                    {partage.origine === 'PARTIES' ? t('offres.partage.deduite')
                        : partage.origine === 'DOSSIER' ? t('offres.partage.tauxDossier') : t('offres.partage.parDefaut')}
                </Tag>
            </div>
            {/* Le détail « 100 % moins X 75 % » et le rappel de plafond ont été retirés
                (recette du 2026-09-15) : ne restent que les cas où la part ne vient pas des fiches. */}
            {!(partage.origine === 'PARTIES' && responsables.length) && (
                <div style={{ fontSize: 12, color: NEUTRAL_600, marginTop: 4 }}>
                    {partage.origine === 'PARTIES'
                        ? t('offres.partage.aucuneAutre')
                        : partage.origine === 'DOSSIER'
                            ? t('offres.partage.pasEncoreEtablies')
                            : t('offres.partage.aucunePart')}
                </div>
            )}
            {sansTaux.length > 0 && (
                <div style={{ fontSize: 12, color: WARN_TEXT, marginTop: 4 }}>
                    <WarningOutlined style={{ marginRight: 4 }} />
                    {t('offres.partage.nonEtablie', { noms: sansTaux.join(', ') })}
                </div>
            )}
            {partage.depasse100 && (
                <div style={{ fontSize: 12, color: DANGER, marginTop: 4 }}>
                    <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                    {t('offres.partage.depasse', { taux: fmtTaux(partage.totalParties) })}
                </div>
            )}
        </div>
    );
};

//  Tableau récap offre blessé 
export const TableauOffreBlesse = ({ offre }) => {
    const { t } = useTranslation('dossierFinance');
    if (!offre) return null;

    const chefs = [
        { label: t('offres.chefs.fraisMedicaux'), val: offre.montantFraisMedicaux, art: 'Art. 258' },
        { label: t('offres.chefs.itt'), val: offre.montantItt, art: 'Art. 259' },
        { label: t('offres.chefs.physiologique'), val: offre.montantPrejPhysiologique, art: 'Art. 260-a' },
        { label: t('offres.chefs.economique'), val: offre.montantPrejEconomique, art: 'Art. 260-b' },
        { label: t('offres.chefs.moral'), val: offre.montantPrejMoral, art: 'Art. 260-c' },
        { label: t('offres.chefs.tiercePersonne'), val: offre.montantTiercePersonne, art: 'Art. 261' },
        { label: t('offres.chefs.pretiumDoloris'), val: offre.montantPretiumDoloris, art: 'Art. 262' },
        { label: t('offres.chefs.esthetique'), val: offre.montantPrejEsthetique, art: 'Art. 262' },
        { label: t('offres.chefs.carriere'), val: offre.montantPrejCarriere, art: 'Art. 263' },
        { label: t('offres.chefs.scolaire'), val: offre.montantPrejScolaire, art: 'Art. 263-1' },
        { label: t('offres.chefs.leses'), val: offre.montantPrejLeses, art: 'Art. 229' },
    ].filter(c => c.val > 0);

    return (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
                <tr style={{ borderBottom: `2px solid ${NEUTRAL_200}` }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left', color: NEUTRAL_400, fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', width: 90 }}>{t('offres.tableau.reference')}</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', color: NEUTRAL_400, fontWeight: 600, fontSize: 11, letterSpacing: '0.05em' }}>{t('offres.tableau.chefDePrejudice')}</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', color: NEUTRAL_400, fontWeight: 600, fontSize: 11, letterSpacing: '0.05em' }}>{t('offres.tableau.montant')}</th>
                </tr>
            </thead>
            <tbody>
                {chefs.map((c, i) => (
                    <tr key={c.label} style={{ borderBottom: `1px solid ${NEUTRAL_100}`, background: i % 2 === 0 ? '#fff' : NEUTRAL_50 }}>
                        <td style={{ padding: '7px 10px', color: NEUTRAL_400, fontSize: 11, fontFamily: 'var(--font-main)' }}>{c.art}</td>
                        <td style={{ padding: '7px 10px', color: NEUTRAL_900 }}>{c.label}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: NEUTRAL_900 }}>{formatMontant(c.val)}</td>
                    </tr>
                ))}
                <tr style={{ borderTop: `2px solid ${NEUTRAL_200}` }}>
                    <td /><td style={{ padding: '7px 10px', color: NEUTRAL_600, fontWeight: 600 }}>{t('offres.tableau.totalBrut')}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{formatMontant(offre.totalBrut)}</td>
                </tr>
                <tr style={{ background: NEUTRAL_50 }}>
                    <td /><td style={{ padding: '5px 10px', color: NEUTRAL_400, fontSize: 12 }}>{libelleDeductionRc(offre.tauxPartageRc)}</td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', color: NEUTRAL_400, fontSize: 12 }}>− {formatMontant(offre.totalBrut - offre.totalNet)}</td>
                </tr>
                <tr>
                    <td /><td style={{ padding: '7px 10px', color: NEUTRAL_600, fontWeight: 600 }}>{t('offres.tableau.totalNet')}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{formatMontant(offre.totalNet)}</td>
                </tr>
                {offre.fraisGestion > 0 && (
                    <tr style={{ background: NEUTRAL_50 }}>
                        <td /><td style={{ padding: '5px 10px', color: NEUTRAL_400, fontSize: 12 }}>{t('offres.tableau.fraisGestion')}</td>
                        <td style={{ padding: '5px 10px', textAlign: 'right', color: NEUTRAL_400, fontSize: 12 }}>{formatMontant(offre.fraisGestion)}</td>
                    </tr>
                )}
                <tr style={{ background: ACCENT_LIGHT, borderTop: `2px solid ${ACCENT_BORDER}` }}>
                    <td /><td style={{ padding: '9px 10px', fontWeight: 700, color: ACCENT, fontSize: 13 }}>{t('offres.tableau.montantTotal')}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 800, color: ACCENT, fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{formatMontant(offre.montantTotalOffre)}</td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <td colSpan={3} style={{ padding: '5px 10px', fontSize: 11, color: NEUTRAL_400 }}>
                        {t('offres.tableau.smig', { montant: formatMontant(offre.smigMensuelRetenu) })}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};

//  Tableau récap offre décès 
export const TableauOffreDeces = ({ offre, ayantsDroit }) => {
    const { t } = useTranslation('dossierFinance');
    if (!offre) return null;

    const chefs = [
        { label: t('offres.chefs.fraisFuneraires'), val: offre.montantFraisFuneraires, art: 'Art. 264' },
        { label: t('offres.chefs.economiqueTotal'), val: offre.montantPrejEconomique, art: 'Art. 265' },
        { label: t('offres.chefs.moralTotal'), val: offre.montantPrejMoral, art: 'Art. 266' },
    ];

    return (
        <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                    <tr style={{ borderBottom: `2px solid ${NEUTRAL_200}` }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: NEUTRAL_400, fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', width: 90 }}>{t('offres.tableau.reference')}</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: NEUTRAL_400, fontWeight: 600, fontSize: 11, letterSpacing: '0.05em' }}>{t('offres.tableau.chefDePrejudice')}</th>
                        <th style={{ padding: '6px 10px', textAlign: 'right', color: NEUTRAL_400, fontWeight: 600, fontSize: 11, letterSpacing: '0.05em' }}>{t('offres.tableau.montant')}</th>
                    </tr>
                </thead>
                <tbody>
                    {chefs.filter(c => c.val > 0).map((c, i) => (
                        <tr key={c.label} style={{ borderBottom: `1px solid ${NEUTRAL_100}`, background: i % 2 === 0 ? '#fff' : NEUTRAL_50 }}>
                            <td style={{ padding: '7px 10px', color: NEUTRAL_400, fontSize: 11, fontFamily: 'var(--font-main)' }}>{c.art}</td>
                            <td style={{ padding: '7px 10px', color: NEUTRAL_900 }}>{c.label}</td>
                            <td style={{ padding: '7px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMontant(c.val)}</td>
                        </tr>
                    ))}
                    <tr style={{ borderTop: `2px solid ${NEUTRAL_200}` }}>
                        <td /><td style={{ padding: '7px 10px', color: NEUTRAL_600, fontWeight: 600 }}>{t('offres.tableau.totalBrut')}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{formatMontant(offre.totalBrut)}</td>
                    </tr>
                    <tr style={{ background: NEUTRAL_50 }}>
                        <td /><td style={{ padding: '5px 10px', color: NEUTRAL_400, fontSize: 12 }}>{libelleDeductionRc(offre.tauxPartageRc)}</td>
                        <td style={{ padding: '5px 10px', textAlign: 'right', color: NEUTRAL_400, fontSize: 12 }}>− {formatMontant(offre.totalBrut - offre.totalNet)}</td>
                    </tr>
                    <tr>
                        <td /><td style={{ padding: '7px 10px', color: NEUTRAL_600, fontWeight: 600 }}>{t('offres.tableau.totalNet')}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{formatMontant(offre.totalNet)}</td>
                    </tr>
                    {offre.fraisGestion > 0 && (
                        <tr style={{ background: NEUTRAL_50 }}>
                            <td /><td style={{ padding: '5px 10px', color: NEUTRAL_400, fontSize: 12 }}>{t('offres.tableau.fraisGestion')}</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', color: NEUTRAL_400, fontSize: 12 }}>{formatMontant(offre.fraisGestion)}</td>
                        </tr>
                    )}
                    <tr style={{ background: ACCENT_LIGHT, borderTop: `2px solid ${ACCENT_BORDER}` }}>
                        <td /><td style={{ padding: '9px 10px', fontWeight: 700, color: ACCENT, fontSize: 13 }}>{t('offres.tableau.montantTotal')}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 800, color: ACCENT, fontSize: 15 }}>{formatMontant(offre.montantTotalOffre)}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3} style={{ padding: '5px 10px', fontSize: 11, color: NEUTRAL_400 }}>
                            {t('offres.tableau.smig', { montant: formatMontant(offre.smigMensuelRetenu) })}
                        </td>
                    </tr>
                </tfoot>
            </table>

            {ayantsDroit.length > 0 && (
                <>
                    <Divider orientation="left" style={{ fontSize: 12, color: NEUTRAL_600, borderColor: NEUTRAL_200 }}>
                        {t('offres.ayantsDroit.repartition')}
                    </Divider>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: NEUTRAL_100, borderBottom: `1px solid ${NEUTRAL_200}` }}>
                                {['nomPrenom', 'lien', 'prejEco', 'prejMoral', 'total'].map(h => (
                                    <th key={h} style={{ padding: '6px 10px', textAlign: h === 'total' || h.includes('prej') ? 'right' : 'left', fontSize: 11, color: NEUTRAL_600, fontWeight: 600 }}>{t(`offres.ayantsDroit.${h}`)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {ayantsDroit.map(a => (
                                <tr key={a.ayantDroitTrackingId} style={{ borderBottom: `1px solid ${NEUTRAL_100}` }}>
                                    <td style={{ padding: '6px 10px' }}>{a.nom} {a.prenoms}</td>
                                    <td style={{ padding: '6px 10px' }}>
                                        <Tag style={{ borderRadius: 4, fontSize: 11 }}>{LIEN_OPTIONS.find(l => l.value === a.lien)?.label || a.lien}</Tag>
                                    </td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatMontant(a.montantPe)}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatMontant(a.montantPm)}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: ACCENT }}>{formatMontant(a.montantTotal)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </>
    );
};

//  Document impression (inchangé) 


/* Un seul document d'offre : celui qui part à la victime (WorkflowOffre.genererPdfOffre).
   L'onglet imprimait sa propre page HTML - un second document, proche mais différent de
   ce que la victime recevait (recette du 2026-09-16). */
const imprimer = (victime, offre, ayantsDroit = [], sinistre = null, expertise = null) => {
    ouvrirImpressionOffre(victime, sinistre, offre, 'victime', expertise, ayantsDroit);
};

/* Ce qui entre dans le calcul CIMA, et ce que chaque donnée y change.

   L'agent découvrait après coup qu'une ITT de 30 jours ne rapportait rien faute de
   revenu renseigné, ou qu'un statut d'activité vide supprimait le préjudice de carrière :
   les données étaient éparpillées entre la fiche victime et l'expertise, et leur effet
   n'était dit nulle part. On les réunit ici, modifiables, avec leur effet en clair ; ce
   qui est modifié est enregistré sur la fiche et sur l'expertise avant le calcul.
   Les règles décrites sont celles de CalculCimaServiceImpl. */
const libelleQualification = (q) => (q && q !== 'NEANT' ? q.replace(/_/g, ' ').toLowerCase() : i18n.t('dossierFinance:offres.donnees.neant'));

const EffetDonnee = ({ children, alerte }) => (
    <div style={{ fontSize: 11.5, lineHeight: 1.45, color: alerte ? WARN_TEXT : NEUTRAL_600, margin: '-6px 0 12px' }}>
        {alerte && <WarningOutlined style={{ marginRight: 4 }} />}{children}
    </div>
);

const DonneesDuCalcul = ({ form, estDecede, expertise }) => {
    const { t } = useTranslation('dossierFinance');
    const revenu = Form.useWatch('revenuMensuel', form);
    const statut = Form.useWatch('statutActivite', form);
    const itt = Number(Form.useWatch('dureeIttJours', form)) || 0;
    const ipp = Number(Form.useWatch('tauxIpp', form)) || 0;
    const naissance = Form.useWatch('dateNaissance', form);
    const aRevenu = Number(revenu) > 0;
    const dateRef = expertise?.dateConsolidation ? dayjs(expertise.dateConsolidation) : dayjs();
    const age = naissance ? dateRef.diff(naissance, 'year') : null;

    const champRevenu = (
        <>
            <Form.Item name="revenuMensuel" label={t('offres.donnees.revenu')} style={{ marginBottom: 8 }}>
                <CBInputNumber min={0} step={1000} style={{ width: '100%' }} addonAfter="FCFA" placeholder={t('offres.donnees.nonRenseigne')}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
            </Form.Item>
            {estDecede
                ? <EffetDonnee alerte={!aRevenu}>
                    {aRevenu ? t('offres.donnees.revenuBaseDeces')
                        : t('offres.donnees.revenuAbsentDeces')}
                </EffetDonnee>
                : <EffetDonnee alerte={!aRevenu}>
                    {aRevenu
                        ? `${t('offres.donnees.revenuBase')}${itt > 0 ? ` ${t('offres.donnees.ittIndemnisee', { jours: itt })}` : ''}`
                        : `${t('offres.donnees.revenuAbsent')}${itt > 0 ? ` ${t('offres.donnees.ittNonIndemnisee', { jours: itt })}` : ''}`}
                </EffetDonnee>}
        </>
    );

    return (
        <div style={{ background: NEUTRAL_50, border: `1px solid ${NEUTRAL_200}`, borderRadius: 6, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ ...sectionLabel, marginBottom: 4 }}>
                <UserOutlined /> {t('offres.donnees.titre')}
            </div>
            <div style={{ fontSize: 12, color: NEUTRAL_600, marginBottom: 12 }}>
                {estDecede ? t('offres.donnees.aideDecede') : t('offres.donnees.aideBlesse')}
            </div>

            {estDecede ? champRevenu : (
                <>
                    <Row gutter={12}>
                        <Col span={14}>
                            <Form.Item name="dateNaissance" label={t('offres.donnees.dateNaissance')} style={{ marginBottom: 8 }}
                                rules={[{ required: true, message: t('offres.donnees.obligatoire') }]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                                    disabledDate={d => d && d > dayjs().endOf('day')} />
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item name="sexe" label={t('offres.donnees.sexe')} style={{ marginBottom: 8 }}>
                                <Select allowClear placeholder="-" options={[{ value: 'M', label: t('offres.donnees.masculin') }, { value: 'F', label: t('offres.donnees.feminin') }]} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <EffetDonnee alerte={!naissance}>
                        {naissance
                            ? (expertise?.dateConsolidation ? t('offres.donnees.ageConsolidation', { age }) : t('offres.donnees.ageAujourdhui', { age }))
                            : t('offres.donnees.naissanceObligatoire')}
                    </EffetDonnee>

                    <Form.Item name="statutActivite" label={t('offres.donnees.statutActivite')} style={{ marginBottom: 8 }}>
                        <Select allowClear placeholder={t('offres.donnees.nonRenseigne')} options={STATUTS_ACTIVITE} />
                    </Form.Item>
                    <EffetDonnee alerte={!statut}>
                        {statut === 'ACTIF' ? t('offres.donnees.carriere')
                            : statut === 'ELEVE_ETUDIANT' ? t('offres.donnees.scolaire')
                                : statut ? t('offres.donnees.niCarriereNiScolaire')
                                    : t('offres.donnees.statutAbsent')}
                    </EffetDonnee>

                    {champRevenu}

                    {expertise ? (
                        <>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item name="dureeIttJours" label={t('offres.donnees.ittJours')} style={{ marginBottom: 8 }}>
                                        <CBInputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="tauxIpp" label={t('offres.donnees.tauxIpp')} style={{ marginBottom: 8 }}>
                                        <CBInputNumber min={0} max={100} style={{ width: '100%' }} placeholder="0" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <EffetDonnee alerte={itt > 0 && !aRevenu}>
                                {itt > 0
                                    ? t('offres.donnees.ittFormule', { jours: itt })
                                    : t('offres.donnees.aucuneItt')}
                                {' '}
                                {ipp === 0 ? t('offres.donnees.ippZero')
                                    : ipp < 50 ? t('offres.donnees.ippMoins50')
                                        : ipp < 80 ? t('offres.donnees.ipp50')
                                            : t('offres.donnees.ipp80')}
                            </EffetDonnee>
                            <div style={{ fontSize: 11.5, color: NEUTRAL_600 }}>
                                {t('offres.donnees.consolidation')} <strong>{expertise.dateConsolidation ? dayjs(expertise.dateConsolidation).format('DD/MM/YYYY') : t('offres.donnees.nonRenseignee')}</strong>
                                {' · '}{t('offres.donnees.pretiumDoloris')} <strong>{libelleQualification(expertise.pretiumDoloris)}</strong>
                                {' · '}{t('offres.donnees.esthetique')} <strong>{libelleQualification(expertise.prejudiceEsthetique)}</strong>
                                {' · '}{t('offres.donnees.tiercePersonne')} <strong>{expertise.necessiteTiercePersonne ? t('offres.donnees.oui') : t('offres.donnees.non')}</strong>
                                <div style={{ color: NEUTRAL_400 }}>{t('offres.donnees.notePretium')}</div>
                            </div>
                        </>
                    ) : (
                        <Alert type="warning" showIcon style={{ borderRadius: 6, fontSize: 12 }}
                            message={t('offres.donnees.aucuneExpertise')}
                            description={t('offres.donnees.aucuneExpertiseTexte')} />
                    )}
                </>
            )}
        </div>
    );
};

/** Ce que l'agent a changé dans la section, enregistré avant de lancer le calcul. */
const enregistrerDonneesDuCalcul = async (vals, victime, expertise, estDecede) => {
    const victimeMaj = {};
    const naissance = vals.dateNaissance ? vals.dateNaissance.format('YYYY-MM-DD') : null;
    if (naissance && naissance !== (victime.dateNaissance || null)) victimeMaj.dateNaissance = naissance;
    if (vals.sexe && vals.sexe !== victime.sexe) victimeMaj.sexe = vals.sexe;
    if (vals.statutActivite && vals.statutActivite !== victime.statutActivite) victimeMaj.statutActivite = vals.statutActivite;
    if (vals.revenuMensuel != null && Number(vals.revenuMensuel) !== Number(victime.revenuMensuel || 0)) {
        victimeMaj.revenuMensuel = vals.revenuMensuel;
    }
    if (Object.keys(victimeMaj).length) {
        await axiosInstance.put(`/v1/victimes/${victime.victimeTrackingId}`, victimeMaj, { skipErrorToast: true });
    }
    if (!estDecede && expertise?.expertiseMedTrackingId) {
        const itt = vals.dureeIttJours ?? 0;
        const ipp = vals.tauxIpp ?? 0;
        if (Number(itt) !== Number(expertise.dureeIttJours || 0) || Number(ipp) !== Number(expertise.tauxIpp || 0)) {
            await axiosInstance.put(`/v1/expertises-medicales/${expertise.expertiseMedTrackingId}`, {
                typeExpertise: expertise.typeExpertise,
                dateDemande: expertise.dateDemande,
                victimeTrackingId: victime.victimeTrackingId,
                dureeIttJours: itt,
                tauxIpp: ipp,
            }, { skipErrorToast: true });
        }
    }
    return Object.keys(victimeMaj).length > 0;
};

//  Modal pré-calcul 
const ModalPreCalcul = ({ open, onCancel, onConfirm, victime, sinistre, partage, loading, expertise, onDonneesEnregistrees, ayantsDroit = null }) => {
    const { t } = useTranslation('dossierFinance');
    const [form] = Form.useForm();
    const [enregistrement, setEnregistrement] = useState(false);
    const estDecede = victime?.typeVictime === 'DECEDE';
    const part = partDeLaVictime(partage, victime);
    const tauxRcSinistre = part?.tauxApplique ?? sinistre?.tauxRc;

    useEffect(() => {
        if (!open || !victime) return;
        form.setFieldsValue({
            dateNaissance: victime.dateNaissance ? dayjs(victime.dateNaissance) : undefined,
            sexe: victime.sexe || undefined,
            statutActivite: victime.statutActivite || undefined,
            revenuMensuel: Number(victime.revenuMensuel) > 0 ? Number(victime.revenuMensuel) : undefined,
            dureeIttJours: Number(expertise?.dureeIttJours) > 0 ? Number(expertise.dureeIttJours) : undefined,
            tauxIpp: Number(expertise?.tauxIpp) > 0 ? Number(expertise.tauxIpp) : undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleOk = async () => {
        const vals = await form.validateFields();
        setEnregistrement(true);
        try {
            await enregistrerDonneesDuCalcul(vals, victime, expertise, estDecede);
        } catch (e) {
            toast.error(t('offres.calcul.donneesNonEnregistrees', { message: e?.response?.data?.message || t('offres.calcul.erreur') }));
            setEnregistrement(false);
            return;
        }
        setEnregistrement(false);
        onDonneesEnregistrees?.();
        const lancer = () => onConfirm({
            fraisMedicaux: estDecede ? null : (vals.fraisMedicaux ?? null),
            fraisFuneraires: estDecede ? (vals.fraisFuneraires ?? null) : null,
            tauxRcOverride: vals.tauxRcOverride ?? null,
        });
        /* Dernier garde-fou AVANT le calcul : un taux à 0 % produit une offre à
           0 FCFA, un décès sans ayants droit un préjudice à 0. Le bandeau
           d'insuffisances en haut de l'onglet se lit trop tard ou pas du tout -
           ici, on oblige à choisir en connaissance de cause. */
        const alertes = [];
        const tauxEffectif = vals.tauxRcOverride ?? tauxRcSinistre;
        if (tauxEffectif != null && Number(tauxEffectif) === 0) {
            alertes.push(t('offres.calcul.alerteTauxZero'));
        }
        if (estDecede && Array.isArray(ayantsDroit) && ayantsDroit.length === 0) {
            alertes.push(t('offres.calcul.alerteSansAyantDroit'));
        }
        if (alertes.length) {
            Modal.confirm({
                title: t('offres.calcul.alerteTitre'),
                icon: <WarningOutlined style={{ color: '#b91c1c' }} />,
                content: <ul style={{ paddingLeft: 18, margin: 0 }}>{alertes.map(a => <li key={a} style={{ marginBottom: 6 }}>{a}</li>)}</ul>,
                okText: t('offres.calcul.calculerQuandMeme'),
                okButtonProps: { danger: true },
                cancelText: t('offres.calcul.completerDabord'),
                onOk: lancer,
            });
            return;
        }
        lancer();
    };

    return (
        <Modal
            title={
                <span style={{ fontWeight: 600, color: NEUTRAL_900 }}>
                    <CalculatorOutlined style={{ color: ACCENT, marginRight: 8 }} />
                    {t('offres.calcul.titre', { nom: victime?.nom, prenoms: victime?.prenoms })}
                </span>
            }
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={loading || enregistrement}
            okText={t('offres.calcul.lancer')}
            cancelText={t('offres.calcul.annuler')}
            okButtonProps={{ style: { background: ACCENT, borderColor: ACCENT }, icon: <CalculatorOutlined /> }}
            width={560}
            destroyOnClose
        >
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <DonneesDuCalcul form={form} estDecede={estDecede} expertise={expertise} />
                {/* Taux RC */}
                <div style={{
                    background: NEUTRAL_50,
                    border: `1px solid ${NEUTRAL_200}`,
                    borderRadius: 6,
                    padding: '12px 14px',
                    marginBottom: 16,
                }}>
                    <div style={{ ...sectionLabel, marginBottom: 8 }}>
                        <SafetyOutlined /> {t('offres.calcul.tauxResponsabilite')}
                    </div>
                    {tauxRcSinistre !== null && tauxRcSinistre !== undefined
                        ? <div style={{ fontSize: 13, color: NEUTRAL_900, marginBottom: 10 }}>
                            {t('offres.calcul.tauxVictime')} <strong style={{ color: ACCENT }}>{Number(tauxRcSinistre)} %</strong>
                            <span style={{ color: NEUTRAL_400, fontSize: 12, marginLeft: 8 }}>{t('offres.calcul.appliqueParDefaut')}</span>
                            {partage && (
                                <div style={{ color: NEUTRAL_600, fontSize: 12, marginTop: 2 }}>{origineTaux(partage, part)}</div>
                            )}
                        </div>
                        : <div style={{ fontSize: 12, color: WARN_TEXT, fontWeight: 600, marginBottom: 10 }}>
                            <WarningOutlined style={{ marginRight: 4 }} />
                            {t('offres.calcul.tauxNonRenseigne')}
                        </div>
                    }
                    <Form.Item
                        name="tauxRcOverride"
                        label={<span style={{ fontSize: 12, color: NEUTRAL_600 }}>{t('offres.calcul.override')}</span>}
                        style={{ marginBottom: 0 }}
                    >
                        {/* Au barème (0, 25, 50, 75, 100 %) ; vide = valeur du sinistre. */}
                        <Select
                            allowClear
                            style={{ width: '100%' }}
                            placeholder={tauxRcSinistre != null ? t('offres.calcul.parDefaut', { taux: Number(tauxRcSinistre) }) : t('offres.calcul.choisirTaux')}
                            options={OPTIONS_TAUX_RC}
                        />
                    </Form.Item>
                </div>

                {!estDecede && (
                    <Form.Item
                        name="fraisMedicaux"
                        label={
                            <Space>
                                <span>{t('offres.calcul.fraisMedicaux')}</span>
                                <Popover
                                    trigger="click"
                                    title={t('offres.calcul.art258.titre')}
                                    content={
                                        <div style={{ maxWidth: 440, fontSize: 12.5, lineHeight: 1.65, color: '#374151' }}>
                                            <p style={{ margin: '0 0 8px' }}>{t('offres.calcul.art258.p1')}</p>
                                            <p style={{ margin: '0 0 8px' }}>{t('offres.calcul.art258.p2')}</p>
                                            <p style={{ margin: '0 0 8px' }}>{t('offres.calcul.art258.p3')}</p>
                                            <p style={{ margin: 0 }}>{t('offres.calcul.art258.p4')}</p>
                                        </div>
                                    }
                                >
                                    <Tag icon={<InfoCircleOutlined />} style={{ borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Art. 258</Tag>
                                </Popover>
                                <Tooltip title={t('offres.calcul.fraisMedicauxAide')}>
                                    <InfoCircleOutlined style={{ color: NEUTRAL_400 }} />
                                </Tooltip>
                            </Space>
                        }
                        extra={<span style={{ fontSize: 11, color: NEUTRAL_400 }}>{t('offres.calcul.fraisMedicauxVide')}</span>}
                    >
                        <CBInputNumber
                            min={0} step={1000} style={{ width: '100%' }}
                            placeholder={t('offres.calcul.fraisMedicauxExemple')} addonAfter="FCFA"
                            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                            parser={v => v.replace(/\s/g, '')}
                        />
                    </Form.Item>
                )}

                {estDecede && (
                    <Form.Item
                        name="fraisFuneraires"
                        label={
                            <Space>
                                <span>{t('offres.calcul.fraisFuneraires')}</span>
                                <Tag style={{ borderRadius: 4, fontSize: 11 }}>Art. 264</Tag>
                                <Tooltip title={t('offres.calcul.fraisFunerairesAide')}>
                                    <InfoCircleOutlined style={{ color: NEUTRAL_400 }} />
                                </Tooltip>
                            </Space>
                        }
                        extra={<span style={{ fontSize: 11, color: NEUTRAL_400 }}>{t('offres.calcul.fraisFunerairesVide')}</span>}
                    >
                        <CBInputNumber
                            min={0} step={1000} style={{ width: '100%' }}
                            placeholder={t('offres.calcul.fraisFunerairesExemple')} addonAfter="FCFA"
                            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                            parser={v => v.replace(/\s/g, '')}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

//  En-tête victime (commun) ─
/**
 * Pourquoi cette offre ne peut plus être recalculée - `null` si elle le peut.
 *
 * Un recalcul crée une NOUVELLE offre, avec un workflow vierge : recalculer une
 * offre déjà transmise ou déjà acceptée laisserait l'accord de la victime
 * accroché à une ligne devenue invisible, et c'est exactement ce qui faisait
 * réapparaître « l'avant-dernière offre ». Le rejet fait exception - recalculer
 * est justement la suite normale d'un désaccord.
 *
 * Même règle que le backend (IndemnisationServiceImpl#assurerRecalculPossible) :
 * ici pour griser le bouton, là-bas pour refuser l'appel.
 */
const motifRecalculImpossible = (offre, sinistre = null) => {
    if (!offre) return null;
    // TE : sur l'offre de l'homologue, le calcul est une évaluation pour information,
    // toujours possible - il ne touche ni l'offre ni son circuit.
    if (offre.origineOffre === 'HOMOLOGUE') return null;
    if (offre.dateRejetVictime) return null;
    if (offre.dateAccordVictime)
        return i18n.t('dossierFinance:offres.recalcul.accordVictime', { date: formatDate(offre.dateAccordVictime) });
    if (offre.dateEnvoiHomologue) {
        const te = sinistre?.typeSinistre === 'SURVENU_ETRANGER';
        const date = formatDate(offre.dateEnvoiHomologue);
        return te ? i18n.t('dossierFinance:offres.recalcul.transmiseCompagnie', { date }) : i18n.t('dossierFinance:offres.recalcul.transmiseHomologue', { date });
    }
    return null;
};

/* TE : l'offre arrive chiffrée du bureau homologue. Le BNCB l'enregistre telle quelle,
   la transmet à la compagnie ; un calcul CIMA n'est ensuite qu'une évaluation. */
const ModalOffreHomologue = ({ open, onClose, victime, sinistre, onEnregistree }) => {
    const { t } = useTranslation('dossierFinance');
    const [form] = Form.useForm();
    const [fichier, setFichier] = useState(null);
    const [envoi, setEnvoi] = useState(false);
    const valider = async () => {
        const v = await form.validateFields();
        setEnvoi(true);
        try {
            const gedId = await uploadGed(fichier, `Offre du bureau homologue - ${sinistre?.numeroSinistreLocal} - ${victime.nom} ${victime.prenoms || ''}`.trim(),
                'OFFRE', sinistre, victime.victimeTrackingId);
            const res = await axiosInstance.post(`/v1/indemnisation/offre-homologue/${victime.victimeTrackingId}`, {
                montant: v.montant,
                dateReception: v.dateReception.format('YYYY-MM-DD'),
                ossanGedDocumentId: gedId != null ? String(gedId) : null,
            });
            toast.success(res.data?.message || t('offres.homologue.enregistree'));
            setFichier(null);
            onEnregistree?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('offres.homologue.nonEnregistree'));
        } finally { setEnvoi(false); }
    };
    return (
        <Modal open={open} onCancel={onClose} onOk={valider} confirmLoading={envoi} destroyOnClose width={520}
            title={<span style={{ fontWeight: 600 }}><FileTextOutlined style={{ color: ACCENT, marginRight: 8 }} />{t('offres.homologue.titre')}</span>}
            okText={t('offres.homologue.enregistrer')} cancelText={t('offres.homologue.annuler')}
            okButtonProps={{ style: { background: ACCENT, borderColor: ACCENT } }}>
            <div style={{ fontSize: 12.5, color: NEUTRAL_600, margin: '8px 0 14px' }}>
                {t('offres.homologue.aide', {
                    bureau: sinistre?.organismeHomologueRaisonSociale || t('offres.homologue.leBureau'),
                    nom: victime.nom, prenoms: victime.prenoms, compagnie: sinistre?.organismeMembreRaisonSociale || '',
                })}
            </div>
            <Form form={form} layout="vertical" initialValues={{ dateReception: dayjs() }}>
                <Row gutter={12}>
                    <Col span={14}>
                        <Form.Item name="montant" label={t('offres.homologue.montant')} rules={[{ required: true, message: t('offres.homologue.montantRequis') }]}>
                            <CBInputNumber min={1} style={{ width: '100%' }} addonAfter="FCFA"
                                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
                        </Form.Item>
                    </Col>
                    <Col span={10}>
                        <Form.Item name="dateReception" label={t('offres.homologue.recueLeLibelle')} rules={[{ required: true, message: t('offres.homologue.dateRequise') }]}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={d => d && d > dayjs().endOf('day')} />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider style={{ fontSize: 12 }}>{t('offres.homologue.courrier')}</Divider>
                <GedFileField value={fichier} onChange={setFichier} maxSizeMo={10} />
            </Form>
        </Modal>
    );
};

/* Résumé d'une offre reçue de l'homologue, et de l'évaluation BNCB quand il y en a une. */
export const ResumeOffreHomologue = ({ offre, sinistre }) => {
    const { t } = useTranslation('dossierFinance');
    return (offre?.origineOffre !== 'HOMOLOGUE' ? null : (
    <Alert type="info" showIcon style={{ borderRadius: 6, marginBottom: 12, fontSize: 12 }}
        message={<span><Trans t={t} i18nKey="offres.homologue.resume"
            values={{ bureau: sinistre?.organismeHomologueRaisonSociale || '', montant: formatMontant(offre.montantTotalOffre) }}
            components={{ b: <strong /> }} />
            {offre.dateReceptionHomologue && <> {t('offres.homologue.recueLe', { date: formatDate(offre.dateReceptionHomologue) })}</>}</span>}
        description={offre.montantEvaluationBncb != null
            ? <span><Trans t={t} i18nKey="offres.homologue.evaluation"
                values={{
                    evaluation: formatMontant(offre.montantEvaluationBncb),
                    ecart: `${Number(offre.montantEvaluationBncb) > Number(offre.montantTotalOffre) ? '+' : ''}${formatMontant(Number(offre.montantEvaluationBncb) - Number(offre.montantTotalOffre))}`,
                }}
                components={{ b: <strong /> }} /></span>
            : t('offres.homologue.sansEvaluation')} />
    ));
};

const VictimeHeader = ({ victime, offre, typeIcon, typeLabel, typeColor, actions }) => {
    const { t } = useTranslation('dossierFinance');
    const sc = statutConfig(offre);
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
        }}>
            {/* Identité */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div style={{
                    width: 36, height: 36,
                    borderRadius: 6,
                    background: NEUTRAL_100,
                    border: `1px solid ${NEUTRAL_200}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: NEUTRAL_600, fontSize: 16, flexShrink: 0,
                }}>
                    {typeIcon}
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: NEUTRAL_900 }}>
                            {victime.nom} {victime.prenoms}
                        </span>
                        <Tag style={{ borderRadius: 4, fontSize: 11, fontWeight: 600, borderColor: typeColor, color: typeColor, background: `${typeColor}10` }}>
                            {typeLabel}
                        </Tag>
                        <Badge status={sc.color} text={<span style={{ fontSize: 11, color: NEUTRAL_600 }}>{sc.label}</span>} />
                    </div>
                    <div style={{ fontSize: 12, color: NEUTRAL_400, marginTop: 2, display: 'flex', gap: 12 }}>
                        <span><UserOutlined style={{ marginRight: 4 }} />{victime.sexe}</span>
                        <span><CalendarOutlined style={{ marginRight: 4 }} />{t('offres.victime.neLe', { date: formatDate(victime.dateNaissance) })}</span>
                        <span><DollarOutlined style={{ marginRight: 4 }} />{t('offres.victime.parMois', { montant: formatMontant(victime.revenuMensuel) })}</span>
                    </div>
                </div>
            </div>
            {/* Actions */}
            <Space wrap size={6}>{actions}</Space>
        </div>
    );
};

//  Insuffisances du dossier (corporel)
/**
 * Ce qui manque au dossier avant de pouvoir faire une offre, victime par victime.
 *
 * Un corporel ne se chiffre pas sans l'expertise médicale et sans ce que le calcul
 * CIMA lit sur la fiche. On le dit en haut, près des articles, plutôt qu'un « Aucune
 * expertise médicale » au fond de chaque carte ; et une expertise faite mais
 * incomplète est signalée pour être complétée.
 *
 * Les champs chiffrés d'une expertise valent 0 par défaut : un IPP à 0 ne prouve pas
 * qu'il manque. Le rapport non reçu (`dateRapport`) est le vrai signal.
 */
const manquesBlesse = (v, expertise) => {
    const bloquants = [];
    const aCompleter = [];
    if (!expertise) {
        bloquants.push(i18n.t('dossierFinance:offres.manques.expertiseAbsente'));
    } else {
        if (!expertise.dateRapport) {
            bloquants.push(i18n.t('dossierFinance:offres.manques.rapportNonRecu'));
        } else if (!Number(expertise.tauxIpp) && !Number(expertise.dureeIttJours)
            && (!expertise.pretiumDoloris || expertise.pretiumDoloris === 'NEANT')
            && (!expertise.prejudiceEsthetique || expertise.prejudiceEsthetique === 'NEANT')) {
            aCompleter.push(i18n.t('dossierFinance:offres.manques.rapportVide'));
        }
        if (!expertise.dateConsolidation) {
            bloquants.push(i18n.t('dossierFinance:offres.manques.consolidationAbsente'));
        }
    }
    if (!v.dateNaissance) bloquants.push(i18n.t('dossierFinance:offres.manques.naissanceAbsente'));
    if (!Number(v.revenuMensuel)) aCompleter.push(i18n.t('dossierFinance:offres.manques.revenuAbsent'));
    if (!v.statutActivite) aCompleter.push(i18n.t('dossierFinance:offres.manques.statutAbsent'));
    return { bloquants, aCompleter };
};

const manquesDecede = (v, ayantsDroit) => {
    const bloquants = [];
    const aCompleter = [];
    if (!ayantsDroit.length) {
        bloquants.push(i18n.t('dossierFinance:offres.manques.aucunAyantDroit'));
    } else {
        const sansDate = ayantsDroit.filter(a => !a.dateNaissance);
        if (sansDate.length) {
            bloquants.push(i18n.t('dossierFinance:offres.manques.naissanceAyantDroit', { noms: sansDate.map(a => `${a.nom || ''} ${a.prenoms || ''}`.trim() || i18n.t('dossierFinance:offres.manques.unAyantDroit')).join(', ') }));
        }
    }
    if (!Number(v.revenuMensuel)) aCompleter.push(i18n.t('dossierFinance:offres.manques.revenuDefuntAbsent'));
    return { bloquants, aCompleter };
};

const InsuffisancesDossier = ({ blesses, decedes }) => {
    const { t } = useTranslation('dossierFinance');
    const [etat, setEtat] = useState(null);
    const cle = [...blesses, ...decedes].map(v => v.victimeTrackingId).join('|');

    useEffect(() => {
        let ignore = false;
        Promise.all([
            ...blesses.map(v => axiosInstance.get(`/v1/expertises-medicales/victime/${v.victimeTrackingId}`, { skipErrorToast: true })
                .then(r => {
                    const exps = r.data?.data || [];
                    return [v.victimeTrackingId, [...exps].sort((a, b) => new Date(b.dateRapport || 0) - new Date(a.dateRapport || 0))[0] || null];
                })
                .catch(() => [v.victimeTrackingId, null])),
            ...decedes.map(v => axiosInstance.get(`/v1/indemnisation/ayants-droit/victime/${v.victimeTrackingId}`, { skipErrorToast: true })
                .then(r => [v.victimeTrackingId, r.data?.data || []])
                .catch(() => [v.victimeTrackingId, []])),
        ]).then(paires => { if (!ignore) setEtat(Object.fromEntries(paires)); });
        return () => { ignore = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cle]);

    if (!etat) return <div style={{ marginBottom: 16 }}><Spin size="small" /> <span style={{ fontSize: 12, color: NEUTRAL_600 }}>{t('offres.insuffisances.verification')}</span></div>;

    const constats = [
        ...blesses.map(v => ({ v, ...manquesBlesse(v, etat[v.victimeTrackingId]) })),
        ...decedes.map(v => ({ v, ...manquesDecede(v, etat[v.victimeTrackingId] || []) })),
    ].filter(c => c.bloquants.length || c.aCompleter.length);

    if (!constats.length) {
        return (
            <Alert type="success" showIcon icon={<CheckCircleOutlined />} style={{ borderRadius: 6, marginBottom: 16, fontSize: 12 }}
                message={t('offres.insuffisances.complet')} />
        );
    }
    return (
        <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 6, background: WARN_BG, border: `1px solid ${WARN_BORDER}` }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: WARN_TEXT, marginBottom: 8 }}>
                <WarningOutlined style={{ marginRight: 6 }} />
                {t('offres.insuffisances.titre')}
            </div>
            {constats.map(({ v, bloquants, aCompleter }) => (
                <div key={v.victimeTrackingId} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: NEUTRAL_900 }}>
                        {`${v.nom || ''} ${v.prenoms || ''}`.trim() || t('offres.insuffisances.victime')}
                        <span style={{ fontWeight: 400, color: NEUTRAL_600 }}> · {v.typeVictime === 'DECEDE' ? t('offres.insuffisances.decede') : t('offres.insuffisances.blesse')}</span>
                    </div>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.6 }}>
                        {bloquants.map(t => <li key={t} style={{ color: DANGER }}>{t}</li>)}
                        {aCompleter.map(t => <li key={t} style={{ color: WARN_TEXT }}>{t}</li>)}
                    </ul>
                </div>
            ))}
        </div>
    );
};

//  Panneau expertise médicale 
const ExpertiseResume = ({ expertise }) => {
    const { t } = useTranslation('dossierFinance');
    // Sans expertise, rien ici : « Insuffisances du dossier », en haut de l'onglet, le dit.
    if (!expertise) return null;

    const items = [
        { label: t('offres.expertise.ipp'), value: `${expertise.tauxIpp ?? 0} %` },
        { label: t('offres.expertise.itt'), value: t('offres.expertise.jours', { jours: expertise.dureeIttJours ?? 0 }) },
        { label: t('offres.expertise.pretiumDoloris'), value: (expertise.pretiumDoloris || 'NEANT').replace(/_/g, ' ') },
        { label: t('offres.expertise.esthetique'), value: (expertise.prejudiceEsthetique || 'NEANT').replace(/_/g, ' ') },
        { label: t('offres.expertise.tiercePersonne'), value: expertise.necessiteTiercePersonne ? t('offres.expertise.oui') : t('offres.expertise.non') },
    ];

    return (
        <div style={{ marginBottom: 14 }}>
            <div style={sectionLabel}><AuditOutlined />{t('offres.expertise.titre')}</div>
            <div style={{ display: 'flex', gap: 0, border: `1px solid ${NEUTRAL_200}`, borderRadius: 6, overflow: 'hidden' }}>
                {items.map((item, i) => (
                    <div key={item.label} style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRight: i < items.length - 1 ? `1px solid ${NEUTRAL_200}` : 'none',
                        background: '#fff',
                        textAlign: 'center',
                    }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: NEUTRAL_900, fontVariantNumeric: 'tabular-nums' }}>
                            {item.value}
                        </div>
                        <div style={{ fontSize: 11, color: NEUTRAL_400, marginTop: 2 }}>{item.label}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

//  Résumé financier offre 
const ResumeFinancier = ({ offre, penalites, danger = false }) => {
    const { t } = useTranslation('dossierFinance');
    return (
    <div style={{
        display: 'flex', gap: 12, alignItems: 'flex-start',
        flexWrap: 'wrap', marginTop: 14,
        padding: '14px 16px',
        background: NEUTRAL_50,
        border: `1px solid ${NEUTRAL_200}`,
        borderRadius: 6,
    }}>
        {amountCard(offre.montantTotalOffre, t('offres.resume.montantTotal'), danger)}
        <div style={{ fontSize: 12, color: NEUTRAL_600, paddingTop: 12 }}>
            <div>{t('offres.resume.brut', { montant: formatMontant(offre.totalBrut) })}</div>
            <div>{t('offres.resume.apresRc', { taux: offre.tauxPartageRc ?? 100, montant: formatMontant(offre.totalNet) })}</div>
        </div>
        {penalites > 0 && (
            <Tooltip title={t('offres.resume.penalitesAide')}>
                <div style={{
                    padding: '10px 16px',
                    background: WARN_BG, border: `1px solid ${WARN_BORDER}`,
                    borderRadius: 6, minWidth: 130,
                }}>
                    <div style={{ fontSize: 11, color: WARN_TEXT, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <ClockCircleOutlined /> {t('offres.resume.penalites')}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: WARN_TEXT }}>{formatMontant(penalites)}</div>
                </div>
            </Tooltip>
        )}
        {offre.dateValidation && (
            <div style={{ paddingTop: 12 }}>
                <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 4, fontWeight: 600 }}>
                    {t('offres.resume.valideeLe', { date: new Date(offre.dateValidation).toLocaleDateString(localeIntl()) })}
                </Tag>
            </div>
        )}
    </div>
    );
};

//  Carte victime blessée ─
const CarteVictimeBlesse = ({ victime, sinistre, partage, onVictimesMaj, onTransformerEnReglement }) => {
    const { t } = useTranslation('dossierFinance');
    const [modalHomologue, setModalHomologue] = useState(false);
    const [offre, setOffre] = useState(null);
    const [penalites, setPenalites] = useState(null);
    const [expertise, setExpertise] = useState(null);
    const [loading, setLoading] = useState(true);
    const [calcLoading, setCalcLoading] = useState(false);
    const [validLoading, setValidLoading] = useState(false);
    const navigate = useNavigate();
    const [modalCalc, setModalCalc] = useState(false);
    const [lastParams, setLastParams] = useState(null);

    const charger = useCallback(async () => {
        setLoading(true);
        try {
            const [offreRes, expRes] = await Promise.allSettled([
                axiosInstance.get(`/v1/indemnisation/offre/victime/${victime.victimeTrackingId}`),
                axiosInstance.get(`/v1/expertises-medicales/victime/${victime.victimeTrackingId}`),
            ]);
            const o = offreRes.status === 'fulfilled' ? offreRes.value.data?.data : null;
            setOffre(o || null);
            const exps = expRes.status === 'fulfilled' ? (expRes.value.data?.data || []) : [];
            const derniere = [...exps].sort((a, b) => new Date(b.dateRapport || 0) - new Date(a.dateRapport || 0))[0];
            setExpertise(derniere || null);
            if (o?.offreTrackingId) {
                const p = await axiosInstance.get(`/v1/indemnisation/penalites/${o.offreTrackingId}`).catch(() => null);
                setPenalites(p?.data?.data ?? null);
            }
        } finally { setLoading(false); }
    }, [victime.victimeTrackingId]);

    useEffect(() => { charger(); }, [charger]);

    const calculer = async (params) => {
        setCalcLoading(true); setModalCalc(false);
        try {
            await axiosInstance.post(`/v1/indemnisation/calculer/${victime.victimeTrackingId}`, params);
            setLastParams(params);
            toast.success(t('offres.cartes.calculee'));
            await charger();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('offres.cartes.erreurCalcul'));
        } finally { setCalcLoading(false); }
    };

    const valider = async () => {
        if (!offre?.offreTrackingId) return;
        setValidLoading(true);
        try {
            await axiosInstance.patch(`/v1/indemnisation/offre/${offre.offreTrackingId}/valider`);
            toast.success(t('offres.cartes.validee'));
            await charger();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('offres.cartes.erreurValidation'));
        } finally { setValidLoading(false); }
    };

    const blocageRecalcul = motifRecalculImpossible(offre, sinistre);
    const estTe = sinistre?.typeSinistre === 'SURVENU_ETRANGER';

    const actions = [
        // « Etablir une offre » plutot que « Calculer » : c'est le nom de l'acte metier,
        // celui qui figure desormais au menu. « Calculer » decrivait le moyen.
        // Bouton PRIMAIRE tant qu'aucune offre n'existe - c'est l'action attendue a ce
        // stade ; une fois l'offre etablie il redevient secondaire, la validation prend
        // le relais.
        <Tooltip key="calc" title={blocageRecalcul || ''}>
            <span>
                <Button icon={<CalculatorOutlined />} loading={calcLoading}
                    type={offre ? 'default' : 'primary'}
                    onClick={() => setModalCalc(true)}
                    disabled={Boolean(blocageRecalcul)}
                    style={offre || blocageRecalcul
                        ? { borderColor: blocageRecalcul ? undefined : ACCENT, color: blocageRecalcul ? undefined : ACCENT, borderRadius: 6 }
                        : { background: ACCENT, borderColor: ACCENT, borderRadius: 6 }}
                    size="small">
                    {offre?.origineOffre === 'HOMOLOGUE' ? t('offres.cartes.evaluerCima') : offre ? t('offres.cartes.recalculer') : t('offres.cartes.etablir')}
                </Button>
            </span>
        </Tooltip>,
        ...(estTe && !offre?.dateEnvoiHomologue && !offre?.dateAccordVictime ? [
            <Button key="homologue" icon={<FileTextOutlined />} size="small"
                type={offre ? 'default' : 'primary'}
                style={offre ? { borderRadius: 6 } : { background: ACCENT, borderColor: ACCENT, borderRadius: 6 }}
                onClick={() => setModalHomologue(true)}>
                {offre?.origineOffre === 'HOMOLOGUE' ? t('offres.cartes.corrigerHomologue') : t('offres.cartes.offreHomologue')}
            </Button>,
        ] : []),
        ...(!offre?.dateValidation && offre ? [
            <Button key="valid" icon={<CheckCircleOutlined />} type="primary" loading={validLoading} onClick={valider}
                style={{ background: ACCENT, borderColor: ACCENT, borderRadius: 6 }} size="small">
                {t('offres.cartes.valider')}
            </Button>
        ] : []),
        ...(offre ? [
            <Button key="detail" icon={<FileTextOutlined />} size="small" style={{ borderRadius: 6 }}
                onClick={() => navigate(`/sinistres/${sinistre?.sinistreTrackingId}/offre/${victime.victimeTrackingId}`)}>{t('offres.cartes.detailCalcul')}</Button>,
            <Button key="print" icon={<PrinterOutlined />} size="small" style={{ borderRadius: 6 }} onClick={() => imprimer(victime, offre, [], sinistre, expertise)}>{t('offres.cartes.imprimer')}</Button>,
        ] : []),
    ];

    return (
        <div style={panelStyle}>
            {/* En-tête */}
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${NEUTRAL_200}` }}>
                <VictimeHeader
                    victime={victime} offre={offre}
                    typeIcon={<MedicineBoxOutlined />}
                    typeLabel={t('offres.cartes.blesse')}
                    typeColor="#ea580c"
                    actions={actions}
                />
            </div>

            {/* Corps */}
            <div style={{ padding: '16px 20px' }}>
                {loading
                    ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                    : <>
                        <BandeauTauxRc sinistre={sinistre} partage={partage} victime={victime} tauxRcOverride={lastParams?.tauxRcOverride} />
                        <ExpertiseResume expertise={expertise} />

                        {offre
                            ? <><ResumeOffreHomologue offre={offre} sinistre={sinistre} /><ResumeFinancier offre={offre} penalites={penalites} danger={false} /></>
                            : <Text type="secondary" style={{ fontSize: 13 }}>
                                <Trans t={t} i18nKey="offres.cartes.cliquezCalculer" components={{ b: <strong /> }} />
                            </Text>
                        }
                        <WorkflowOffre offre={offre} victime={victime} sinistre={sinistre} onRefresh={charger} expertise={expertise} onTransformerEnReglement={onTransformerEnReglement} />
                    </>
                }
            </div>

            <ModalOffreHomologue open={modalHomologue} onClose={() => setModalHomologue(false)}
                victime={victime} sinistre={sinistre} onEnregistree={charger} />

            <ModalPreCalcul open={modalCalc} onCancel={() => setModalCalc(false)}
                onConfirm={calculer} victime={victime} sinistre={sinistre} partage={partage} loading={calcLoading}
                expertise={expertise} onDonneesEnregistrees={onVictimesMaj} />

        </div>
    );
};

//  Carte victime décédée + ayants droit ─
const CarteVictimeDeces = ({ victime, sinistre, partage, onVictimesMaj, onTransformerEnReglement }) => {
    const { t } = useTranslation('dossierFinance');
    const [modalHomologue, setModalHomologue] = useState(false);
    const [offre, setOffre] = useState(null);
    const [ayantsDroit, setAyantsDroit] = useState([]);
    const [penalites, setPenalites] = useState(null);
    const [loading, setLoading] = useState(true);
    const [calcLoading, setCalcLoading] = useState(false);
    const [validLoading, setValidLoading] = useState(false);
    const navigate = useNavigate();
    const [modalCalc, setModalCalc] = useState(false);
    const [modalAD, setModalAD] = useState(false);
    /* La fiche en cours de correction, ou null pour un ajout. Le meme formulaire
       sert aux deux : ce sont les memes champs, et les dedoubler ferait deux
       listes a tenir en accord. */
    const [adEnEdition, setAdEnEdition] = useState(null);
    const [savingAD, setSavingAD] = useState(false);
    const [lastParams, setLastParams] = useState(null);
    const [formAD] = Form.useForm();

    const charger = useCallback(async () => {
        setLoading(true);
        try {
            const [offreRes, adRes] = await Promise.allSettled([
                axiosInstance.get(`/v1/indemnisation/offre/victime/${victime.victimeTrackingId}`),
                axiosInstance.get(`/v1/indemnisation/ayants-droit/victime/${victime.victimeTrackingId}`),
            ]);
            const o = offreRes.status === 'fulfilled' ? offreRes.value.data?.data : null;
            setOffre(o || null);
            setAyantsDroit(adRes.status === 'fulfilled' ? (adRes.value.data?.data || []) : []);
            if (o?.offreTrackingId) {
                const p = await axiosInstance.get(`/v1/indemnisation/penalites/${o.offreTrackingId}`).catch(() => null);
                setPenalites(p?.data?.data ?? null);
            }
        } finally { setLoading(false); }
    }, [victime.victimeTrackingId]);

    useEffect(() => { charger(); }, [charger]);

    const calculer = async (params) => {
        setCalcLoading(true); setModalCalc(false);
        try {
            await axiosInstance.post(`/v1/indemnisation/calculer/${victime.victimeTrackingId}`, params);
            setLastParams(params);
            toast.success(t('offres.cartes.calculeeDeces'));
            await charger();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('offres.cartes.erreurCalcul'));
        } finally { setCalcLoading(false); }
    };

    const valider = async () => {
        if (!offre?.offreTrackingId) return;
        setValidLoading(true);
        try {
            await axiosInstance.patch(`/v1/indemnisation/offre/${offre.offreTrackingId}/valider`);
            toast.success(t('offres.cartes.validee'));
            await charger();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('offres.cartes.erreurValidation'));
        } finally { setValidLoading(false); }
    };

    const ouvrirAjoutAD = () => {
        setAdEnEdition(null);
        formAD.resetFields();
        setModalAD(true);
    };

    /* Corriger plutot que supprimer puis resaisir : une date de naissance fautive
       obligeait a retaper les six champs, et autant d'occasions de se tromper sur
       les cinq autres. */
    const ouvrirEditionAD = (ad) => {
        setAdEnEdition(ad);
        formAD.setFieldsValue({
            nom: ad.nom,
            prenoms: ad.prenoms,
            dateNaissance: ad.dateNaissance ? dayjs(ad.dateNaissance) : null,
            sexe: ad.sexe,
            lien: ad.lien,
            estOrphelinDouble: !!ad.estOrphelinDouble,
            poursuiteEtudes: !!ad.poursuiteEtudes,
        });
        setModalAD(true);
    };

    const enregistrerAD = async (vals) => {
        setSavingAD(true);
        const corps = {
            ...vals,
            dateNaissance: vals.dateNaissance?.format('YYYY-MM-DD'),
            victimeTrackingId: victime.victimeTrackingId,
            estOrphelinDouble: vals.estOrphelinDouble ?? false,
            poursuiteEtudes: vals.poursuiteEtudes ?? false,
        };
        try {
            if (adEnEdition) {
                await axiosInstance.put(
                    `/v1/indemnisation/ayants-droit/${adEnEdition.ayantDroitTrackingId}`, corps);
                toast.success(t('offres.cartes.ayantDroitModifie'));
            } else {
                await axiosInstance.post('/v1/indemnisation/ayants-droit', corps);
                toast.success(t('offres.cartes.ayantDroitAjoute'));
            }
            setModalAD(false); setAdEnEdition(null); formAD.resetFields(); await charger();
        } catch (e) {
            toast.error(e?.response?.data?.message
                || t(adEnEdition ? 'offres.cartes.erreurModification' : 'offres.cartes.erreurAjout'));
        } finally { setSavingAD(false); }
    };

    const supprimerAD = async (id) => {
        try {
            await axiosInstance.delete(`/v1/indemnisation/ayants-droit/${id}`);
            toast.success(t('offres.cartes.ayantDroitSupprime'));
            await charger();
        } catch (e) {
            toast.error(e?.response?.data?.message || t('offres.cartes.erreurSuppression'));
        }
    };

    const colsAD = [
        { title: t('offres.ayantsDroit.nomPrenom'), key: 'np', width: 160, render: (_, r) => `${r.nom} ${r.prenoms}` },
        { title: t('offres.ayantsDroit.lien'), dataIndex: 'lien', width: 110, render: v => <Tag style={{ borderRadius: 4, fontSize: 11 }}>{LIEN_OPTIONS.find(l => l.value === v)?.label || v}</Tag> },
        { title: t('offres.ayantsDroit.dateNaissance'), dataIndex: 'dateNaissance', width: 110, render: v => formatDate(v) },
        { title: t('offres.ayantsDroit.orphelin'), dataIndex: 'estOrphelinDouble', width: 80, align: 'center', render: v => v ? <Tag color="red" style={{ borderRadius: 4, fontSize: 11 }}>{t('offres.ayantsDroit.oui')}</Tag> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text> },
        { title: t('offres.ayantsDroit.etudes'), dataIndex: 'poursuiteEtudes', width: 65, align: 'center', render: v => v ? <Tag color="blue" style={{ borderRadius: 4, fontSize: 11 }}>{t('offres.ayantsDroit.oui')}</Tag> : <Text type="secondary" style={{ fontSize: 12 }}>-</Text> },
        { title: t('offres.ayantsDroit.prejEco'), dataIndex: 'montantPe', align: 'right', width: 120, render: v => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>{formatMontant(v)}</span> },
        { title: t('offres.ayantsDroit.prejMoral'), dataIndex: 'montantPm', align: 'right', width: 120, render: v => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>{formatMontant(v)}</span> },
        { title: t('offres.ayantsDroit.total'), dataIndex: 'montantTotal', align: 'right', width: 130, render: v => <strong style={{ color: ACCENT, fontVariantNumeric: 'tabular-nums' }}>{formatMontant(v)}</strong> },
        {
            title: '', key: 'actions', width: 72, align: 'center',
            render: (_, r) => (
                <Space size={0}>
                    <Tooltip title={t('offres.ayantsDroit.modifier')}>
                        <Button type="text" icon={<EditOutlined />} size="small"
                            onClick={() => ouvrirEditionAD(r)} />
                    </Tooltip>
                    <Popconfirm title={t('offres.ayantsDroit.supprimerConfirm')} onConfirm={() => supprimerAD(r.ayantDroitTrackingId)}
                        okText={t('offres.ayantsDroit.supprimer')} cancelText={t('offres.ayantsDroit.annuler')} okButtonProps={{ danger: true }}>
                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const blocageRecalcul = motifRecalculImpossible(offre, sinistre);
    const estTe = sinistre?.typeSinistre === 'SURVENU_ETRANGER';

    const actions = [
        <Button key="ad" icon={<PlusOutlined />} size="small" style={{ borderRadius: 6 }}
            onClick={ouvrirAjoutAD}>
            {t('offres.cartes.ayantDroit')}
        </Button>,
        // « Etablir une offre » plutot que « Calculer » : c'est le nom de l'acte metier,
        // celui qui figure desormais au menu. « Calculer » decrivait le moyen.
        // Bouton PRIMAIRE tant qu'aucune offre n'existe - c'est l'action attendue a ce
        // stade ; une fois l'offre etablie il redevient secondaire, la validation prend
        // le relais.
        <Tooltip
            key="calc"
            title={blocageRecalcul
                || (ayantsDroit.length === 0 ? t('offres.cartes.ajouterAyantDroitAvant') : '')}>
            <span>
                <Button icon={<CalculatorOutlined />} loading={calcLoading}
                    type={offre || ayantsDroit.length === 0 ? 'default' : 'primary'}
                    onClick={() => setModalCalc(true)}
                    disabled={ayantsDroit.length === 0 || Boolean(blocageRecalcul)}
                    style={offre || ayantsDroit.length === 0 || blocageRecalcul
                        ? { borderColor: blocageRecalcul ? undefined : ACCENT, color: blocageRecalcul ? undefined : ACCENT, borderRadius: 6 }
                        : { background: ACCENT, borderColor: ACCENT, borderRadius: 6 }}
                    size="small">
                    {offre?.origineOffre === 'HOMOLOGUE' ? t('offres.cartes.evaluerCima') : offre ? t('offres.cartes.recalculer') : t('offres.cartes.etablir')}
                </Button>
            </span>
        </Tooltip>,
        ...(estTe && !offre?.dateEnvoiHomologue && !offre?.dateAccordVictime ? [
            <Button key="homologue" icon={<FileTextOutlined />} size="small"
                type={offre ? 'default' : 'primary'}
                style={offre ? { borderRadius: 6 } : { background: ACCENT, borderColor: ACCENT, borderRadius: 6 }}
                onClick={() => setModalHomologue(true)}>
                {offre?.origineOffre === 'HOMOLOGUE' ? t('offres.cartes.corrigerHomologue') : t('offres.cartes.offreHomologue')}
            </Button>,
        ] : []),
        ...(!offre?.dateValidation && offre ? [
            <Button key="valid" icon={<CheckCircleOutlined />} type="primary" loading={validLoading} onClick={valider}
                style={{ background: ACCENT, borderColor: ACCENT, borderRadius: 6 }} size="small">
                {t('offres.cartes.valider')}
            </Button>
        ] : []),
        ...(offre ? [
            <Button key="detail" icon={<FileTextOutlined />} size="small" style={{ borderRadius: 6 }}
                onClick={() => navigate(`/sinistres/${sinistre?.sinistreTrackingId}/offre/${victime.victimeTrackingId}`)}>{t('offres.cartes.detailCalcul')}</Button>,
            <Button key="print" icon={<PrinterOutlined />} size="small" style={{ borderRadius: 6 }} onClick={() => imprimer(victime, offre, ayantsDroit, sinistre)}>{t('offres.cartes.imprimer')}</Button>,
        ] : []),
    ];

    return (
        <div style={{ ...panelStyle, borderColor: '#fca5a5' }}>
            {/* En-tête */}
            <div style={{ padding: '14px 20px', borderBottom: `1px solid #fce7e7`, background: '#fffafa' }}>
                <VictimeHeader
                    victime={victime} offre={offre}
                    typeIcon={<FileProtectOutlined />}
                    typeLabel={t('offres.cartes.decede')}
                    typeColor={DANGER}
                    actions={actions}
                />
            </div>

            {/* Corps */}
            <div style={{ padding: '16px 20px' }}>
                {loading
                    ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                    : <>
                        <BandeauTauxRc sinistre={sinistre} partage={partage} victime={victime} tauxRcOverride={lastParams?.tauxRcOverride} />

                        {ayantsDroit.length === 0
                            ? <Alert type="info" showIcon icon={<TeamOutlined />}
                                style={{ borderRadius: 6, marginBottom: 14, fontSize: 12 }}
                                message={t('offres.cartes.aucunAyantDroit')}
                                description={t('offres.cartes.aucunAyantDroitTexte')}
                            />
                            : <>
                                <div style={{ ...sectionLabel, marginBottom: 10 }}>
                                    <TeamOutlined /> {t('offres.cartes.ayantsDroit', { nombre: ayantsDroit.length })}
                                </div>
                                <Table
                                    className="cb-table"
                                    dataSource={ayantsDroit}
                                    columns={colsAD}
                                    rowKey="ayantDroitTrackingId"
                                    size="small"
                                    pagination={false}
                                    style={{ marginBottom: 14, border: `1px solid ${NEUTRAL_200}`, borderRadius: 6 }}
                                />
                            </>
                        }

                        {offre
                            ? <><ResumeOffreHomologue offre={offre} sinistre={sinistre} /><ResumeFinancier offre={offre} penalites={penalites} danger /></>
                            : ayantsDroit.length > 0
                                ? <Text type="secondary" style={{ fontSize: 13 }}><Trans t={t} i18nKey="offres.cartes.cliquezCalculerDeces" components={{ b: <strong /> }} /></Text>
                                : null
                        }
                        <WorkflowOffre offre={offre} victime={victime} sinistre={sinistre} onRefresh={charger} ayantsDroit={ayantsDroit} onTransformerEnReglement={onTransformerEnReglement} />
                    </>
                }
            </div>

            <ModalOffreHomologue open={modalHomologue} onClose={() => setModalHomologue(false)}
                victime={victime} sinistre={sinistre} onEnregistree={charger} />

            <ModalPreCalcul open={modalCalc} onCancel={() => setModalCalc(false)}
                onConfirm={calculer} victime={victime} sinistre={sinistre} partage={partage} loading={calcLoading}
                onDonneesEnregistrees={onVictimesMaj} ayantsDroit={ayantsDroit} />

            {/* Ajout ET correction d'un ayant droit : memes champs, meme formulaire. */}
            <Modal
                title={<span style={{ fontWeight: 600 }}><TeamOutlined style={{ marginRight: 8, color: ACCENT }} />
                    {t(adEnEdition ? 'offres.ajoutAyantDroit.titreModification' : 'offres.ajoutAyantDroit.titre')}</span>}
                open={modalAD} onCancel={() => { setModalAD(false); setAdEnEdition(null); }}
                onOk={() => formAD.submit()} confirmLoading={savingAD}
                okText={t('offres.ajoutAyantDroit.enregistrer')} okButtonProps={{ style: { background: ACCENT, borderColor: ACCENT } }}
                width={560} destroyOnClose
            >
                {/* Les montants deja calcules restent attaches a la fiche : corriger une
                    date de naissance les rend caducs, puisque l'age commande le calcul. */}
                {adEnEdition && (
                    <Alert type="info" showIcon style={{ marginBottom: 12 }}
                        message={t('offres.ajoutAyantDroit.recalculNecessaire')} />
                )}
                <Form form={formAD} layout="vertical" onFinish={enregistrerAD} style={{ marginTop: 8 }}>
                    <Row gutter={14}>
                        <Col span={12}>
                            <Form.Item name="nom" label={t('offres.ajoutAyantDroit.nom')} rules={[{ required: true }]}>
                                <input className="ant-input" style={{ width: '100%', padding: '4px 11px', border: `1px solid ${NEUTRAL_200}`, borderRadius: 6 }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="prenoms" label={t('offres.ajoutAyantDroit.prenoms')} rules={[{ required: true }]}>
                                <input className="ant-input" style={{ width: '100%', padding: '4px 11px', border: `1px solid ${NEUTRAL_200}`, borderRadius: 6 }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={14}>
                        <Col span={12}>
                            <Form.Item name="dateNaissance" label={t('offres.ajoutAyantDroit.dateNaissance')} rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="sexe" label={t('offres.ajoutAyantDroit.sexe')} rules={[{ required: true }]}>
                                <Select options={[{ value: 'M', label: 'M' }, { value: 'F', label: 'F' }]} />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="lien" label={t('offres.ajoutAyantDroit.lien')} rules={[{ required: true }]}>
                                <Select options={LIEN_OPTIONS} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={14}>
                        <Col span={12}>
                            <Form.Item name="estOrphelinDouble" label={t('offres.ajoutAyantDroit.orphelinDouble')} initialValue={false}>
                                <Select options={[{ value: false, label: t('offres.ajoutAyantDroit.non') }, { value: true, label: t('offres.ajoutAyantDroit.oui') }]} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="poursuiteEtudes" label={t('offres.ajoutAyantDroit.poursuiteEtudes')} initialValue={false}>
                                <Select options={[{ value: false, label: t('offres.ajoutAyantDroit.non') }, { value: true, label: t('offres.ajoutAyantDroit.ouiMoins25') }]} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Drawer détail */}
        </div>
    );
};

//  Composant principal 
export default function OngletOffresIndemnisation({ sinistre, victimes = [], onVictimesMaj, onTransformerEnReglement }) {
    const { t } = useTranslation('dossierFinance');
    // Une partie adverse blessée ou décédée s'indemnise comme une victime.
    const victimesSeules = victimes.filter(estVictimeIndemnisable);
    const blesses = victimesSeules.filter(v => v.typeVictime === 'BLESSE');
    const decedes = victimesSeules.filter(v => v.typeVictime === 'DECEDE');
    const corporelCount = blesses.length + decedes.length;

    // Partage des responsabilités : il bouge avec les parts saisies sur les fiches.
    const [partage, setPartage] = useState(null);
    const clePartage = victimes.map(v => `${v.victimeTrackingId}:${v.tauxRc ?? ''}`).join('|');
    useEffect(() => {
        let ignore = false;
        const sid = sinistre?.sinistreTrackingId;
        if (!sid) { setPartage(null); return undefined; }
        axiosInstance.get(`/v1/sinistres/${sid}/partage-responsabilite`, { skipErrorToast: true })
            .then(r => { if (!ignore) setPartage(r.data?.data || null); })
            .catch(() => { if (!ignore) setPartage(null); });
        return () => { ignore = true; };
    }, [sinistre?.sinistreTrackingId, sinistre?.tauxRc, clePartage]);

    // Préjudice matériel : les expertises matérielles du sinistre (montant dit expert = indemnité évaluée).
    const [expertisesMat, setExpertisesMat] = useState([]);
    const [loadingMat, setLoadingMat] = useState(true);
    useEffect(() => {
        let ignore = false;
        const sid = sinistre?.sinistreTrackingId;
        if (!sid) { setLoadingMat(false); return undefined; }
        setLoadingMat(true);
        axiosInstance.get(`/v1/expertises-materielles/sinistre/${sid}`)
            .then(r => { if (!ignore) setExpertisesMat(r.data?.data || []); })
            .catch(() => { if (!ignore) setExpertisesMat([]); })
            .finally(() => { if (!ignore) setLoadingMat(false); });
        return () => { ignore = true; };
    }, [sinistre?.sinistreTrackingId]);

    const colsMat = [
        {
            title: t('offres.materiel.vehicule'), key: 'vehicule',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600, color: NEUTRAL_900 }}>
                        {[r.marqueVehicule, r.modeleVehicule].filter(Boolean).join(' ') || '-'}
                    </div>
                    <div style={{ fontSize: 11, color: NEUTRAL_600 }}>{r.immatriculation || '-'}</div>
                </div>
            ),
        },
        { title: t('offres.materiel.natureDegats'), dataIndex: 'natureDommages', ellipsis: true, render: (v) => v || '-' },
        {
            title: t('offres.materiel.etat'), key: 'vei', width: 110, align: 'center',
            render: (_, r) => r.estVei
                ? <Tag color="red" style={{ borderRadius: 20 }}>{t('offres.materiel.vei')}</Tag>
                : <Tag color="green" style={{ borderRadius: 20 }}>{t('offres.materiel.reparable')}</Tag>,
        },
        { title: t('offres.materiel.valeurVenale'), dataIndex: 'valeurVenal', align: 'right', width: 130, render: (v) => formatMontant(v) },
        {
            title: t('offres.materiel.montantExpert'), dataIndex: 'montantDitExpert', align: 'right', width: 150,
            render: (v) => <span style={{ fontWeight: 700, color: ACCENT }}>{formatMontant(v)}</span>,
        },
        { title: t('offres.materiel.expert'), dataIndex: 'expertNomComplet', ellipsis: true, render: (v) => v || '-' },
        {
            title: t('offres.materiel.rapport'), dataIndex: 'rapportRecu', width: 110, align: 'center',
            render: (v) => v
                ? <Tag color="green" style={{ borderRadius: 20 }}>{t('offres.materiel.recu')}</Tag>
                : <Tag style={{ borderRadius: 20 }}>{t('offres.materiel.enAttente')}</Tag>,
        },
    ];

    // Libellé d'onglet : icône + titre + compteur (badge vert si non vide).
    const tabLabel = (icon, titre, count) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {icon}{titre}
            <Tag color={count ? 'green' : 'default'} style={{ borderRadius: 20, fontWeight: 600, margin: 0 }}>{count}</Tag>
        </span>
    );

    const contenuCorporel = (
        <div>
            {/* Bandeau informatif CIMA (propre au corporel) */}
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ borderRadius: 6, marginBottom: 20, fontSize: 12 }}
                message={<strong>{t('offres.bandeauCima.titre')}</strong>}
                description={
                    <span style={{ fontSize: 12 }}>
                        <Trans t={t} i18nKey="offres.bandeauCima.cliquez" components={{ b: <strong /> }} />
                        {' '}&ensp;·&ensp;<Trans t={t} i18nKey="offres.bandeauCima.delaiBlesse" components={{ b: <strong /> }} />
                        &ensp;·&ensp;<Trans t={t} i18nKey="offres.bandeauCima.delaiDeces" components={{ b: <strong /> }} />
                        &ensp;·&ensp;<Trans t={t} i18nKey="offres.bandeauCima.penalite" components={{ b: <strong /> }} />
                    </span>
                }
            />
            {corporelCount > 0 && <InsuffisancesDossier blesses={blesses} decedes={decedes} />}
            {corporelCount > 0 && <ResumePartage partage={partage} />}
            {corporelCount === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span style={{ color: NEUTRAL_600 }}>{t('offres.aucuneVictime')}</span>} />
            ) : (
                <>
                    {/* Section blessés */}
                    {blesses.length > 0 && (
                        <section style={{ marginBottom: 20 }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                marginBottom: 14, paddingBottom: 8, borderBottom: `1px solid ${NEUTRAL_200}`,
                            }}>
                                <MedicineBoxOutlined style={{ color: NEUTRAL_600, fontSize: 16 }} />
                                <span style={{ fontWeight: 600, fontSize: 14, color: NEUTRAL_900 }}>{t('offres.victimesBlessees')}</span>
                                <Tag style={{ borderRadius: 4, fontWeight: 600 }}>{blesses.length}</Tag>
                            </div>
                            {blesses.map(v => <CarteVictimeBlesse key={v.victimeTrackingId} victime={v} sinistre={sinistre} partage={partage} onVictimesMaj={onVictimesMaj} onTransformerEnReglement={onTransformerEnReglement} />)}
                        </section>
                    )}

                    {/* Section décédés */}
                    {decedes.length > 0 && (
                        <section>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                marginBottom: 14, paddingBottom: 8, borderBottom: `1px solid ${NEUTRAL_200}`,
                            }}>
                                <FileProtectOutlined style={{ color: NEUTRAL_600, fontSize: 16 }} />
                                <span style={{ fontWeight: 600, fontSize: 14, color: NEUTRAL_900 }}>{t('offres.victimesDecedees')}</span>
                                <Tag style={{ borderRadius: 4, fontWeight: 600 }}>{decedes.length}</Tag>
                            </div>
                            {decedes.map(v => <CarteVictimeDeces key={v.victimeTrackingId} victime={v} sinistre={sinistre} partage={partage} onVictimesMaj={onVictimesMaj} onTransformerEnReglement={onTransformerEnReglement} />)}
                        </section>
                    )}
                </>
            )}
        </div>
    );

    const contenuMateriel = loadingMat ? (
        <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
    ) : (
        <Table
            className="cb-table"
            dataSource={expertisesMat}
            columns={colsMat}
            rowKey="expertiseMaTrackingId"
            size="small"
            pagination={false}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('offres.materiel.aucuneExpertise')} /> }}
        />
    );

    return (
        <div style={{ padding: 24, maxWidth: 1000 }}>
            <Tabs
                defaultActiveKey="corporel"
                items={[
                    { key: 'corporel', label: tabLabel(<MedicineBoxOutlined />, t('offres.onglets.corporel'), corporelCount), children: contenuCorporel },
                    { key: 'materiel', label: tabLabel(<CarOutlined />, t('offres.onglets.materiel'), expertisesMat.length), children: contenuMateriel },
                ]}
            />
        </div>
    );
}